import {
  Activity,
  ArrowDownToLine,
  ArrowLeftRight,
  Banknote,
  BookCopy,
  Image as ImageIcon,
  LayoutDashboard,
  LineChart,
  LogOut,
  MessageCircleQuestion,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  Sliders,
  Timer,
  Users,
  UserX,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_SUBPAGES } from '@/pages/AdminApp';

const ICONS = {
  dashboard: LayoutDashboard,
  users: Users,
  profit: LineChart,
  deposits: ArrowDownToLine,
  payouts: Banknote,
  tickets: MessageCircleQuestion,
  ad_manager: ImageIcon,
  audit: BookCopy,
  system: Sliders,
  subpack_settings: Timer,
  active_subpacks: Activity,
  proof_of_work: ShieldCheck,
  launch_control: Rocket,
  threat: ShieldAlert,
  cheaters: UserX,
};

// Subpages that ONLY the god account can see. Sub-admins (is_admin=true,
// is_god_account=false) get these items entirely hidden from the nav —
// the backend also 403s direct API calls, so this is purely cosmetic.
const GOD_ONLY_SUBPAGES = new Set(['profit', 'payouts', 'subpack_settings', 'launch_control', 'cheaters']);

export const AdminSidebar = ({ active, onSelect, admin }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[280px] z-40 flex-col bg-[rgba(8,8,9,0.78)] backdrop-blur-xl border-r border-[rgba(255,182,18,0.12)]"
      data-testid="admin-sidebar"
    >
      {/* Brand */}
      <div className="px-6 pt-6 pb-5 border-b border-[rgba(255,182,18,0.10)]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FFC20E] to-[#FFB612] shadow-[0_0_20px_rgba(255,182,18,0.35)]">
            <ShieldCheck className="w-5 h-5 text-[#0a0a0b]" strokeWidth={2.6} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px]">
              Admin <span className="sc-gold-text">Console</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Oversight Command Matrix</div>
          </div>
        </div>
      </div>

      {/* Admin badge */}
      <div className="px-4 py-4 border-b border-[rgba(255,182,18,0.08)]">
        <div className="sc-glass rounded-xl p-3 flex items-center gap-3" data-testid="admin-sidebar-card">
          <div
            className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center font-display font-bold text-sm"
            style={{
              background: 'linear-gradient(180deg, rgba(255,182,18,0.18), rgba(255,182,18,0.02))',
              border: '1px solid rgba(255, 182, 18, 0.32)',
              color: '#FFE08A',
            }}
          >
            {admin?.username?.slice(0, 2)?.toUpperCase() || 'AD'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{admin?.username || '—'}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFC20E] truncate">{admin?.is_god_account ? 'GOD ADMIN' : 'ADMIN ROLE'}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-1">
          {ADMIN_SUBPAGES.map((s) => {
            // Hide god-only entries for sub-admins.
            if (GOD_ONLY_SUBPAGES.has(s.id) && admin && !admin.is_god_account) {
              return null;
            }
            const Icon = ICONS[s.id];
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelect(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-[14px] transition ${
                  isActive
                    ? 'bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.32)] text-[#FFC20E] font-semibold shadow-[inset_0_0_0_1px_rgba(255,182,18,0.08)]'
                    : 'text-[var(--sc-text-dim)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] border border-transparent'
                }`}
                data-testid={`admin-nav-${s.id}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{s.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="px-3 pb-4 pt-2 border-t border-[rgba(255,182,18,0.08)] space-y-1">
        <button
          type="button"
          onClick={() => navigate('/dashboard', { replace: true })}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] transition"
          data-testid="admin-switch-to-member"
        >
          <ArrowLeftRight className="w-4 h-4" />
          Switch to Member View
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] transition"
          data-testid="admin-logout"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
